import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import { KaraokeTrackManager } from '@src/editor/lib/KaraokeTrackManager';

import { AbstractCommand } from '../AbstractCommand';
import { TimelineMode } from '../TimelineMode';

import type { TranscriptTrackManager } from '@src/editor/lib/TranscriptTrackManager';
import type { HasClipTranscriptSegment } from '@src/editor/models/elements/HasClipTranscriptSegment';
import type { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import type {
  ClipTranscriptTextToken,
  ElementResizeResult,
} from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

/**
 * TranscriptPasteCommand – pastes clipboard transcript segments at a specific
 * position within a VideoTranscriptElement. The command supports:
 *   • Insert before a VideoTranscriptElement (at the start)
 *   • Insert after a VideoTranscriptElement (at the end)
 *   • Insert in the middle of a VideoTranscriptElement (requires splitting)
 *
 * When inserting in the middle of a VideoTranscriptElement, the element is
 * split at the specified word index and the clipboard segments are inserted
 * between the split parts. RemovedTranscriptClips are handled by ensuring
 * inserted segments are placed before them when appropriate.
 */
export class TranscriptPasteCommand extends AbstractCommand {
  getName(): string {
    return 'TranscriptPasteCommand';
  }

  private readonly clipboardSegments: ClipTranscriptSegment[];
  private readonly clipSegmentId: string;
  private readonly atIndex: number; // 0 based index
  private readonly after: boolean; // if true, paste after atIndex (to correctly handle insertion after the last index), else before

  private transcriptTrack: TranscriptTrack | null = null;
  private readonly transcriptTrackManager: TranscriptTrackManager;

  private transcriptTrackElements: readonly HasClipTranscriptSegment[] = [];

  private timelineSegmentAdded: ElementResizeResult | null = null;

  constructor(
    editor: VideoEditor,
    clipboardSegments: ClipTranscriptSegment[],
    clipSegmentId: string,
    atIndex: number,
    after: boolean,
  ) {
    super(editor);

    this.clipboardSegments = clipboardSegments;
    this.clipSegmentId = clipSegmentId;
    this.atIndex = atIndex;
    this.after = after;

    this.transcriptTrackManager = this.editor.getTranscriptTrackManager();

    this.transcriptTrack = editor.getVideoTranscriptTrack() || null;
    this.transcriptTrackElements = this.transcriptTrack
      ? this.transcriptTrack.transcriptSegmentElements
      : [];
  }

  // -------------------------------------------------------------------
  // Command lifecycle
  // -------------------------------------------------------------------

  execute(): boolean {
    const track = this.transcriptTrack;

    if (!track || !this.clipboardSegments.length) {
      console.log(
        'TranscriptPasteCommand: Early return - no track or no segments',
      );
      return false;
    }

    // Find the target video element containing the insertion point
    const targetElement = this.findTargetVideoElement();

    if (!targetElement) {
      console.error('TranscriptPasteCommand: Target video element not found');
      return false;
    }

    // Determine insertion strategy based on position within the element
    const insertedElements = this.performPasteInsertion(targetElement);
    if (!insertedElements) {
      console.log('TranscriptPasteCommand: No elements were inserted');
      return false;
    }

    // After operation is successfull, mark segments as added
    insertedElements.forEach(element =>
      element.getClipTranscriptSegment().markElementsAsAdded(),
    );

    // Recalculate timeline and mark dirty
    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_paste');

    // Reconcile karaoke track after paste operation
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (karaokeTrack) {
      const karaokeTrackManager = new KaraokeTrackManager(this.editor);
      karaokeTrackManager.reconcileKaraokeTrack();
    }

    return true;
  }

  undo(): boolean {
    throw new Error('Method not implemented.');
  }

  getDescription(): string {
    return `Paste transcript segments at ${this.clipSegmentId}[${
      this.atIndex
    }]${this.after ? '+' : ''}`;
  }

  getTimelineSegmentAdded() {
    return this.timelineSegmentAdded;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  /**
   * Finds the target VideoTranscriptElement containing the insertion point
   * Only insertion relative to VideoTranscriptElements is supported
   */
  private findTargetVideoElement(): VideoTranscriptElement | null {
    const targetElement = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.clipSegmentId,
    );

    // Only allow insertion relative to VideoTranscriptElements
    if (targetElement instanceof VideoTranscriptElement) {
      return targetElement;
    }

    return null;
  }

  /**
   * Performs the paste insertion using the appropriate strategy
   * Converts command inputs to generic method arguments
   */
  private performPasteInsertion(
    targetElement: VideoTranscriptElement,
  ): VideoTranscriptElement[] | null {
    const segment = targetElement.getClipTranscriptSegment();
    const wordsWithPunctuation = segment.getWordsWithPunctuation();

    // Determine effective insertion index
    const effectiveIndex = this.after ? this.atIndex + 1 : this.atIndex;

    // Check if we're inserting at boundaries or in the middle
    const isAtStart =
      effectiveIndex <= wordsWithPunctuation.indexOf(segment.firstWord!);
    const isAtEnd =
      effectiveIndex > wordsWithPunctuation.indexOf(segment.lastWord!);

    if (isAtStart) {
      // Insert before the video element
      const insertionTime = targetElement.time;
      return this.transcriptTrackManager.insertTranscriptSegmentsAtTime(
        this.clipboardSegments,
        insertionTime,
        targetElement,
        TimelineMode.RIPPLE,
      );
    } else if (isAtEnd) {
      // Insert after the video element
      const insertionTime = targetElement.end_time;
      return this.transcriptTrackManager.insertTranscriptSegmentsAtTime(
        this.clipboardSegments,
        insertionTime,
        targetElement,
        TimelineMode.RIPPLE,
      );
    } else {
      // Insert in the middle - need to split the video element
      const wordsAfterInsertionPosition = segment
        .getWordsWithPunctuation()
        .filter((_, idx) => idx >= effectiveIndex)
        .filter(
          (token): token is ClipTranscriptTextToken => token.type === 'text',
        );

      if (!wordsAfterInsertionPosition.length) {
        console.error(
          'TranscriptPasteCommand: No words found after insertion position for identifying splitting time',
        );
        return null;
      }

      const splitResult =
        this.transcriptTrackManager.insertTranscriptSegmentsBySplitting(
          targetElement,
          wordsAfterInsertionPosition[0].media_ts,
          this.clipboardSegments,
          TimelineMode.RIPPLE,
        );

      return splitResult ? splitResult.insertedElements : null;
    }
  }
}
